import { useEffect } from "react";
import { isLocal } from "@/helpers/cookie.helper";
import { UserInfo } from "../types/user.types";

let isProductFruitsInitialized = false;
let initializedForUserId: string | null = null;

export function useProductFruits(userDetails: UserInfo | null | undefined, hasPermission: boolean) {
  useEffect(() => {
    if (!userDetails || !hasPermission) {
      return;
    }

    if (!userDetails.email) {
      return;
    }

    // Reset guards if the signed-in user has changed (soft SPA account switch, no full reload).
    if (isProductFruitsInitialized && initializedForUserId !== userDetails.email) {
      const pf = window.productFruits as { services?: { destroy?: () => void } } | undefined;
      pf?.services?.destroy?.();
      isProductFruitsInitialized = false;
      initializedForUserId = null;
      window.productFruitsScriptLoaded = false;
    }

    // Module-level guard: survives re-renders and StrictMode double-invocation
    if (isProductFruitsInitialized) {
      return;
    }

    // Window-level guard: survives HMR module reloads in dev
    if (window.productFruitsScriptLoaded) {
      return;
    }

    const { firstName = "", lastName = "", email = "", createdAt } = userDetails;
    const createdAtDate = createdAt
      ? (typeof createdAt === 'string' && isNaN(Number(createdAt))
          ? createdAt
          : new Date(Number(createdAt)).toISOString())
      : new Date().toISOString();

    const productFruitKey = isLocal
      ? import.meta.env.VITE_PRODUCTFRUIT_LOCAL_KEY
      : import.meta.env.VITE_PRODUCTFRUIT_KEY;

    if (!productFruitKey) {
      console.warn("Product Fruits key not found in environment variables");
      return;
    }

    isProductFruitsInitialized = true;
    window.productFruitsScriptLoaded = true;
    initializedForUserId = userDetails.email;

    // product-fruits is role-gated and non-critical, so keep it out of the eager
    // bundle: load it dynamically only once we've decided to initialize.
    import("product-fruits")
      .then(({ productFruits }) => {
        productFruits.init(
          productFruitKey,
          "en",
          {
            username: [firstName, lastName].filter(Boolean).join(" "),
            firstname: firstName || "",
            lastname: lastName || "",
            email: email || "",
            signUpAt: createdAtDate,
          },
          { disableLocationChangeDetection: false }
        );
      })
      .catch((error) => {
        console.error("Failed to load product-fruits", error);
        // Roll back the guards so a later render can retry the load.
        isProductFruitsInitialized = false;
        initializedForUserId = null;
        window.productFruitsScriptLoaded = false;
      });
    // No cleanup: resetting flags on unmount caused re-initialization on remounts,
    // triggering the "already initialized" warning. The widget persists for the session.
    // User-change reset is handled explicitly above.
  }, [userDetails, hasPermission]);

  return { isInitialized: isProductFruitsInitialized };
}
